import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Info, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PayoutData {
  hourly_rate: number;
  average_credit_value: number;
  effective_payout_rate: number;
  total_sessions_this_month: number;
  total_earnings_this_month: number;
  last_month_average: number;
}

interface MentorPayoutTransparencyProps {
  mentorId: string;
}

export function MentorPayoutTransparency({ mentorId }: MentorPayoutTransparencyProps) {
  const [payoutData, setPayoutData] = useState<PayoutData | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchPayoutData();
  }, [mentorId]);

  const fetchPayoutData = async () => {
    try {
      setLoading(true);
      
      // Get mentor's hourly rate
      const { data: mentor, error: mentorError } = await supabase
        .from('mentors')
        .select('hourly_rate')
        .eq('id', mentorId)
        .single();

      if (mentorError) throw mentorError;

      // Get completed sessions with credit values for this month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: sessions, error: sessionsError } = await supabase
        .from('sessions')
        .select('credit_value_used, mentor_payout_amount, duration_minutes, completed_at')
        .eq('mentor_id', mentorId)
        .eq('status', 'completed')
        .gte('completed_at', startOfMonth.toISOString());

      if (sessionsError) throw sessionsError;

      // Calculate averages and totals
      const totalSessions = sessions?.length || 0;
      const totalEarnings = sessions?.reduce((sum, session) => sum + (session.mentor_payout_amount || 0), 0) || 0;
      const avgCreditValue = sessions?.length > 0 
        ? sessions.reduce((sum, session) => sum + (session.credit_value_used || mentor.hourly_rate), 0) / sessions.length
        : mentor.hourly_rate; // Keep in cents for calculation

      // Get last month's data for comparison
      const lastMonthStart = new Date(startOfMonth);
      lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);
      const lastMonthEnd = new Date(startOfMonth);

      const { data: lastMonthSessions } = await supabase
        .from('sessions')
        .select('credit_value_used, duration_minutes')
        .eq('mentor_id', mentorId)
        .eq('status', 'completed')
        .gte('completed_at', lastMonthStart.toISOString())
        .lt('completed_at', lastMonthEnd.toISOString());

      const lastMonthAvg = lastMonthSessions?.length > 0
        ? lastMonthSessions.reduce((sum, session) => sum + (session.credit_value_used || mentor.hourly_rate), 0) / lastMonthSessions.length
        : mentor.hourly_rate; // Keep in cents for calculation

      setPayoutData({
        hourly_rate: mentor.hourly_rate / 100, // Convert from cents to dollars
        average_credit_value: avgCreditValue / 100, // Convert from cents to dollars
        effective_payout_rate: (avgCreditValue * 0.925) / 100, // 92.5% after 7.5% platform fee
        total_sessions_this_month: totalSessions,
        total_earnings_this_month: totalEarnings / 100, // Convert from cents to dollars
        last_month_average: lastMonthAvg / 100 // Convert from cents to dollars
      });

    } catch (error: any) {
      console.error('Error fetching payout data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load payout information',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = () => {
    if (!payoutData) return <Minus className="w-4 h-4" />;
    
    const currentRate = payoutData.effective_payout_rate;
    const lastMonthRate = payoutData.last_month_average * 0.925; // Both already converted to dollars
    
    if (currentRate > lastMonthRate) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (currentRate < lastMonthRate) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-gray-600" />;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            Payout Rate Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!payoutData) return null;

  const rateComparison = payoutData.effective_payout_rate - payoutData.hourly_rate;
  const isHigher = rateComparison > 0;
  const isLower = rateComparison < 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="w-5 h-5" />
          Payout Rate Transparency
        </CardTitle>
        <CardDescription>
          Your earnings are based on what students actually paid for credits, ensuring fair compensation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Rates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Your Listed Rate</label>
            <div className="text-2xl font-bold">${payoutData.hourly_rate.toFixed(2)}/hour</div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Current Effective Rate</label>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-green-600">
                ${payoutData.effective_payout_rate.toFixed(2)}/hour
              </div>
              {getTrendIcon()}
            </div>
          </div>
        </div>

        {/* Rate Comparison */}
        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Market Rate vs Your Rate:</span>
            <Badge variant={isHigher ? "default" : isLower ? "secondary" : "outline"}>
              {isHigher && "+"}${Math.abs(rateComparison).toFixed(2)}/hour
              {isHigher && " above your rate"}
              {isLower && " below your rate"}
              {!isHigher && !isLower && " matches your rate"}
            </Badge>
          </div>
        </div>

        {/* This Month Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold">{payoutData.total_sessions_this_month}</div>
            <div className="text-sm text-muted-foreground">Sessions This Month</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">${payoutData.total_earnings_this_month.toFixed(2)}</div>
            <div className="text-sm text-muted-foreground">Total Earnings</div>
          </div>
        </div>

        {/* Transparency Note */}
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">How Payout Rates Work</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• You earn 92.5% of what students actually paid for their credits (7.5% platform fee)</li>
            <li>• Bulk discount pricing benefits students while maintaining your fair payout rate</li>
            <li>• Your effective rate reflects the actual market value students paid per credit</li>
            <li>• This ensures transparent, fair compensation across all credit packages</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}